// Cross-app single sign-on client. Same logic in reserve (TS), inventory and alcoholometry (JS),
// in line with the /sso/* contract in firebase-labox-ws docs/cross-app-sso/README.md.
import { onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth';

const STARTUP_TIMEOUT_MS = 3000;
const REQUEST_TIMEOUT_MS = 5000;
const WATCH_THROTTLE_MS = 10000;

// eslint-disable-next-line no-console
const warn = (message, error) => console.warn(message, error);

// The running client, so that ssoSignOut() can share the sign-out.
let activeClient = null;

export function isSsoHost(hostname = window.location.hostname) {
  // China proxy hosts are under labox-apps.com but /sso/* is not proxied there.
  return hostname.endsWith('.labox-apps.com') && !hostname.startsWith('china-');
}

async function post(route, body, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`/sso/${route}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`SSO ${route} failed with ${response.status}`);
    }
    return response.status === 204 ? null : response.json();
  } finally {
    clearTimeout(timer);
  }
}

// Signs the user out of this app and, through the shared session, of the other apps.
// Only for an explicit sign-out by the user: other sign-outs stay local to the app.
export async function ssoSignOut(auth) {
  if (activeClient) {
    await activeClient.signOut();
  } else {
    await signOut(auth);
  }
}

// Shares the Firebase session with the other *.labox-apps.com apps.
// - Sign-ins this client did not trigger itself create the shared session.
// - ssoSignOut() ends it for the other apps; other sign-outs stay local.
// - On startup and when the tab comes back, the app follows the shared session. When another user
//   is signed in here, onUserChange(switchUser) lets the app ask before switching.
// Network failures never sign anyone out: the local session is kept as is.
export function startSso({
  auth, enabled, onReady, onUserChange = () => {},
}) {
  if (!enabled) {
    const unsubscribe = onAuthStateChanged(auth, () => {
      unsubscribe();
      onReady();
    });
    return unsubscribe;
  }

  let isFirstAuthState = true;
  let currentUid = null;
  // Uid of a sign-in this client is doing itself, so that the listener does not share it again.
  let expectedUid;
  // Uid of a sign-in by the user that is not in the shared session yet.
  let unsharedSignInUid = null;
  let pendingSessionShare = null;
  let pendingSync = null;
  let lastSyncAt = 0;
  let signingOut = false;
  let stopped = false;

  const localUid = () => (auth.currentUser ? auth.currentUser.uid : null);

  const shareSession = (user) => {
    const share = user.getIdToken()
      .then((idToken) => post('session', { idToken }, REQUEST_TIMEOUT_MS))
      .then(() => {
        if (unsharedSignInUid === user.uid) unsharedSignInUid = null;
      });
    pendingSessionShare = share;
    const clear = () => {
      if (pendingSessionShare === share) pendingSessionShare = null;
    };
    share.then(clear, clear);
    return share;
  };

  const signInFromSharedSession = async ({ customToken, uid }) => {
    expectedUid = uid;
    try {
      await signInWithCustomToken(auth, customToken);
    } finally {
      expectedUid = undefined;
    }
  };

  // Switches to the user of the shared session, then restarts the app on a clean state.
  const switchUser = async () => {
    try {
      const result = await post('sync', { uid: null }, REQUEST_TIMEOUT_MS);
      if (result && result.action === 'signIn') {
        await signInFromSharedSession(result);
      }
    } catch (error) {
      warn('SSO user switch failed', error);
    }
    window.location.reload();
  };

  const applySync = async (result, uid) => {
    // A stopped client (unmounted, or StrictMode's first mount) leaves auth alone.
    if (!result || stopped || signingOut) return;
    if (result.action === 'createSession' && auth.currentUser) {
      await shareSession(auth.currentUser);
    } else if (result.action === 'signIn') {
      if (uid) {
        // Another user signed in elsewhere: pending work belongs to the current user, ask first.
        onUserChange(switchUser);
      } else {
        await signInFromSharedSession(result);
      }
    } else if (result.action === 'signOut' && auth.currentUser) {
      await signOut(auth);
      window.location.reload();
    }
  };

  const runSync = async (timeoutMs) => {
    if (pendingSessionShare) await pendingSessionShare.catch(() => {});
    const uid = localUid();
    if (uid && uid === unsharedSignInUid) {
      // The user's own sign-in is not shared yet: retry instead of following an older session.
      await shareSession(auth.currentUser);
      return;
    }
    const result = await post('sync', { uid }, timeoutMs);
    // Skip a stale answer if the user changed while the request was in flight.
    if (localUid() === uid) await applySync(result, uid);
  };

  const syncSession = (timeoutMs = REQUEST_TIMEOUT_MS) => {
    if (!pendingSync) {
      lastSyncAt = Date.now();
      pendingSync = runSync(timeoutMs)
        .catch((error) => warn('SSO sync skipped', error))
        .finally(() => { pendingSync = null; });
    }
    return pendingSync;
  };

  const unsubscribe = onAuthStateChanged(auth, (user) => {
    const uid = user ? user.uid : null;
    const previousUid = currentUid;
    currentUid = uid;

    if (isFirstAuthState) {
      isFirstAuthState = false;
      if (user) {
        // Restored local session: render now, follow the shared session in the background.
        onReady();
        syncSession();
      } else {
        // No local session: wait for a possible shared session before showing the sign-in page.
        syncSession(STARTUP_TIMEOUT_MS).finally(() => {
          if (!stopped) onReady();
        });
      }
      return;
    }
    if (uid === previousUid) return;
    unsharedSignInUid = null;
    if (!user || uid === expectedUid) return;
    unsharedSignInUid = uid;
    shareSession(user).catch((error) => warn('SSO session not created', error));
  });

  const client = {
    signOut: async () => {
      signingOut = true;
      try {
        await post('logout', {}, REQUEST_TIMEOUT_MS)
          .catch((error) => warn('SSO sign-out not shared', error));
        await signOut(auth);
      } finally {
        signingOut = false;
      }
    },
  };
  activeClient = client;

  const onFocus = () => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastSyncAt < WATCH_THROTTLE_MS) return;
    syncSession();
  };
  document.addEventListener('visibilitychange', onFocus);
  window.addEventListener('focus', onFocus);

  return () => {
    stopped = true;
    if (activeClient === client) activeClient = null;
    unsubscribe();
    document.removeEventListener('visibilitychange', onFocus);
    window.removeEventListener('focus', onFocus);
  };
}
